import React from "react";
import { RBACProvider, useAuth, type RBACClient } from "@webuild-ai/react-auth";

import { useWebuildAPIClient } from "./WeBuildClientProvider";

/**
 * RBACProviderWrapper adapts the admin client to the react-auth RBACClient interface.
 *
 * This wrapper:
 * - Gets the authenticated user from AuthUserProvider
 * - Gets the admin client from WeBuildClientProvider
 * - Creates an RBACClient adapter that calls adminClient.rbac.getUserPermissions
 * - Passes the adapter to RBACProvider from react-auth
 *
 * The RBACProvider then fetches permissions and provides them via useRBAC hook.
 */
export const RBACProviderWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { adminClient } = useWebuildAPIClient();

  // Only render provider when both user and client are available
  if (!user?.id || !adminClient) {
    return <>{children}</>;
  }

  // Create adapter that matches RBACClient interface
  const rbacClient: RBACClient = {
    getUserPermissions: async ({ userId }) => {
      return adminClient.rbac.getUserPermissions({ userId });
    },
  };

  return (
    <RBACProvider rbacClient={rbacClient} userId={user.id}>
      {children}
    </RBACProvider>
  );
};
